import { AxiosError } from 'axios'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import CardDisplayBox from 'ext-ui/components/CardDisplayBox/CardDisplayBox'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import Markdown from 'markdown-to-jsx'
import moment from 'moment'
import React, {
  ChangeEvent,
  ComponentProps,
  KeyboardEvent,
  ReactElement,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import {
  usePostCreditCardV2,
  usePutCreditCardV2,
} from 'src/api/mutation/creditcards'
import { AddressFormFields } from 'src/component-form/AddressForm/AddressForm'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import {
  AstradaBinData,
  AstradaCardData,
} from 'src/context/AstradaContext/AstradaContext'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { Userpilot } from 'userpilot'

import PhysicalCardVerifyStep from './PhysicalCardVerifyStep'
import style from './ReviewEnrollCard.style'

export type ReviewEnrollCardProps = {
  className?: string
  /** The submission data to review, broken down into 3 sub objects that follow our
   * create card Form structure
   */
  submissionData: {
    /** Data that comes from the Bin input response - eligibility is accounted for via `AstradaProvider` */
    binData: Omit<AstradaBinData, 'rangeMax' | 'rangeMin' | 'supported'>
    /** Card data that comes from the BinInput form */
    cardData: AstradaCardData
    /** Address data, cardholder data from the `AddressForm` component */
    addressData: AddressFormFields
    /** Extra data specifically for issuers that the API needs. Any dynamic fields need their data captured here */
    issuerData?: { [key: string]: string }
  }
  /**
   * If creditCardId is present, the component will bypass creating a stub card
   * when clicking "confirm" - this value exists under the assumption that the
   * card has already been created and lives in VGS, and we need to perform an verification
   * (like 3DS verification) on this card ID.
   */
  creditCardId?: string
  goBackText?: string
  onBack: () => void
  onVerifyStart?: () => void
  onVerifyComplete?: (cardId: string) => void
  onVerifyError?: () => void
} & ComponentProps<'div'>

type ReviewEnrollCardAcknowledgements = {
  editAccess: boolean
  cardNetwork: boolean
  termsAndConditions: boolean
}
/**
 * Shows a review screen and relevant acknowledgements regarding
 * the user's entered card information
 *
 */
function ReviewEnrollCard(props: ReviewEnrollCardProps): ReactElement {
  const {
    className = '',
    submissionData,
    goBackText,
    onBack,
    onVerifyStart,
    onVerifyError,
    onVerifyComplete,
    ...rest
  } = props

  const { binData, addressData, cardData, issuerData } = submissionData

  // STATE
  const [started3DSVerification, setStarted3DSVerification] =
    useState<string>('')
  const [termsExpanded, setTermsExpanded] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [termsCheckboxes, setTermsCheckboxes] =
    useState<ReviewEnrollCardAcknowledgements>({
      editAccess: false,
      cardNetwork: false,
      termsAndConditions: false,
    })

  // SELECTORS
  const locale = useSelector(selectNullSafeLocaleFromState)
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)

  // API CALLS
  const { mutateAsync: createStubCard } = usePostCreditCardV2()
  const { mutateAsync: putCreditCardV2 } = usePutCreditCardV2()

  // CUSTOM TOOLS
  const translate = useTranslate()

  // EVENT HANDLERS
  const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name as keyof ReviewEnrollCardAcknowledgements
    setTermsCheckboxes({
      ...termsCheckboxes,
      [name]: e.currentTarget.checked,
    })
  }

  const handleSubmitData = async () => {
    setIsSubmitting(true)
    try {
      let creditCardId = props.creditCardId
      if (!creditCardId) {
        // If there is no direct creditCardId passed in as a prop,
        // Create a stub card
        const response = await createStubCard({
          displayName: addressData.cardName,
          country: addressData.country,
          companyName: currentOrganization?.name,
          cardholderName: addressData.cardholderName,
          address1: addressData.address1,
          address2: addressData.address2,
          city: addressData.city,
          postal: addressData.postal,
          province: addressData.province,
          cvc: cardData.cvc,
          issuerId: binData.issuerId,
          issuerFields: {
            ...(issuerData || {}),
            cardNumber: cardData.number.split(' ').join(''),
            expires: moment(
              cardData.expiration,
              getLocaleInfo(locale).shortDateFormat,
            )
              .endOf('month')
              .format('YYYY-MM-DD'),
          },
          organizationId: currentOrganization?.id,
        })
        creditCardId = response.creditCard.id
      }

      if (!creditCardId) {
        throw Error('Missing ID in response')
      }

      if (binData.eligibility === 'VIRTUAL_ONLY') {
        await putCreditCardV2({
          creditCardId: creditCardId,
          endpoint: 'virtual',
        })

        onVerifyComplete?.(creditCardId)
      } else if (binData.eligibility === 'PHYSICAL_ONLY') {
        // <PhysicalCardVerifyStep> does the API calls and opens the window in the
        // useEffect call when mounted/rendered
        // onVerifyComplete is called in <PhysicalCardVerifyStep>
        setStarted3DSVerification(creditCardId)
      } else if (binData.eligibility === 'PHYSICAL_AND_VIRTUAL') {
        // TODO: Finish up the 3rd possible state
      }
    } catch (error: unknown) {
      setTermsExpanded(false)
      setIsSubmitting(false)
      const unwrappedError = error as AxiosError

      //  Casting here as to appease typescript. It is hard to see if there is a generic to send in
      // to appease typescript so I'm casting for the time being
      const errorData = unwrappedError.response?.data as { error?: string }
      if (errorData.error) {
        setErrorMessage(errorData.error)
      } else {
        const defaultErrorMessage = translate(
          'Card enrollment failed - Try again later',
        )
        setErrorMessage(defaultErrorMessage)
      }
    }
  }

  // STATE HELPERS
  const isSubmitDisabled = () => {
    const { binData } = submissionData

    if (binData.eligibility === 'VIRTUAL_ONLY') {
      return termsCheckboxes.editAccess === false
    }
    if (binData.eligibility === 'PHYSICAL_ONLY') {
      return (
        termsCheckboxes.cardNetwork === false ||
        termsCheckboxes.termsAndConditions === false
      )
    }
    if (binData.eligibility === 'PHYSICAL_AND_VIRTUAL') {
      return (
        termsCheckboxes.cardNetwork === false ||
        termsCheckboxes.termsAndConditions === false ||
        termsCheckboxes.editAccess === false
      )
    }

    return true
  }

  return (
    <div className={cn('ReviewEnrollCard', className)} css={style} {...rest}>
      {started3DSVerification ? (
        <PhysicalCardVerifyStep
          cardId={started3DSVerification}
          eligibilityType={submissionData.binData.eligibility}
          onCardEnrollError={(message) => {
            setStarted3DSVerification('')
            setTermsExpanded(false)
            setIsSubmitting(false)
            setErrorMessage(message)
            onVerifyError?.()
          }}
          onVerifyStart={onVerifyStart}
          onVerifyError={(message) => {
            setStarted3DSVerification('')
            setTermsExpanded(false)
            setIsSubmitting(false)
            setErrorMessage(message)
          }}
          onVerifyComplete={onVerifyComplete}
        />
      ) : (
        <div className="flex-column gap-10">
          <p className="font-h2">
            {translate('Please review card details and acknowledge access')}
          </p>
          <div className="midsection flex-column gap">
            <CardDisplayBox
              cardData={{
                cardName: submissionData.addressData.cardName,
                cardholderName: submissionData.addressData.cardholderName,
                number: submissionData.cardData.number,
                expiration: submissionData.cardData.expiration,
                cvc: submissionData.cardData.cvc,
                issuerImg: submissionData.binData?.issuerLogoImg,
                address: {
                  // Address data can be dynamic since each country has a different set of criteria
                  ...submissionData.addressData,
                },
              }}
            />
            <div className="section-acknowledge flex-column gap-5">
              <div className="flex-row justify-between align-center">
                <p className="font-support-medium">
                  {translate('Acknowledgements:')}
                </p>
                <Button
                  variant="no-shell"
                  className="chevron"
                  role="button"
                  onClick={() => {
                    setTermsExpanded(!termsExpanded)
                  }}
                >
                  {termsExpanded ? <SvgChevronDown /> : <SvgChevronUp />}
                </Button>
              </div>
              {termsExpanded && (
                <>
                  {(binData.eligibility === 'VIRTUAL_ONLY' ||
                    binData.eligibility === 'PHYSICAL_AND_VIRTUAL') && (
                    <div className="flex-row gap-3">
                      <Checkbox
                        data-testid="editAccessAcknowledgeCheckbox"
                        name="editAccess"
                        checked={termsCheckboxes.editAccess}
                        onChange={handleCheckboxClick}
                      />
                      <div className="flex-column gap-3">
                        <p className="legal-text">
                          <Markdown options={{ disableParsingRawHTML: true }}>
                            {translate(
                              'I acknowledge that the Owner and any current or future Admin(s) will have the following edit access over your registered card account: ',
                            )}
                          </Markdown>
                        </p>
                        <ul className="legal-text">
                          <li>
                            <Markdown options={{ disableParsingRawHTML: true }}>
                              {translate(
                                'They can create virtual cards and budgets from your account',
                              )}
                            </Markdown>
                          </li>
                          <li>
                            <Markdown options={{ disableParsingRawHTML: true }}>
                              {translate(
                                'They can close virtual cards and budgets under your account',
                              )}
                            </Markdown>
                          </li>
                          <li>
                            <Markdown options={{ disableParsingRawHTML: true }}>
                              {translate(
                                'They can update virtual card and budget details under your account',
                              )}
                            </Markdown>
                          </li>
                        </ul>

                        <p className="legal-text">
                          {translate(
                            "All actions will be logged in the 'History' of each virtual card or budget, including details of the user who performed the action.",
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                  {(binData.eligibility === 'PHYSICAL_ONLY' ||
                    binData.eligibility === 'PHYSICAL_AND_VIRTUAL') && (
                    <>
                      <div className="flex-row gap-3">
                        <Checkbox
                          data-testid="cardNetworkAcknowledgeCheckbox"
                          name="cardNetwork"
                          checked={termsCheckboxes.cardNetwork}
                          onChange={handleCheckboxClick}
                        />
                        <p className="legal-text">
                          <Markdown options={{ disableParsingRawHTML: true }}>
                            {translate(
                              'By submitting your card information and checking this box, you authorize your card network to access, monitor and share transaction data (e.g., amount, time, date, and merchant name) with Astrada (our service provider) for the purposes of identifying relevant transactions and carrying out related spend management services. You also affirm that the card being monitored is used primarily for business purposes.',
                            )}
                          </Markdown>
                        </p>
                      </div>
                      <div className="flex-row gap-3">
                        <Checkbox
                          data-testid="termsAndConditionsAcknowledgeCheckbox"
                          name="termsAndConditions"
                          checked={termsCheckboxes.termsAndConditions}
                          onChange={handleCheckboxClick}
                        />
                        <p className="legal-text">
                          <Markdown options={{ disableParsingRawHTML: true }}>
                            {translate(
                              "By submitting your card information and checking this box, you affirm your consent so that Astrada may process said data in accordance with Extend's Terms and Conditions, Extend's Privacy Policy, and Astrada's Privacy Policy. You also acknowledge and agree that relevant transaction details may be shared with involved third parties to enable spend management processes, in accordance with Astrada's Privacy Policy. You may opt out of transaction monitoring on the payment card you enter at any time by going to your account settings.",
                            )}
                          </Markdown>
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {errorMessage && (
            <AlertPanel variant="severe">
              {errorMessage && (
                <div className="flex-row justify-start gap-4">
                  <AlertTriangleIcon />
                  <div className="flex-column gap-4">
                    <p className="font-bold">
                      {translate('There was an error registering your card')}
                    </p>
                    <p className="">
                      {errorMessage === 'ERROR_FAILED' ? (
                        <>
                          {translate('Please try again or ')}
                          <span
                            role="button"
                            tabIndex={-1}
                            onKeyDown={(e: KeyboardEvent) => {
                              if (e.key === 'Enter') {
                                Userpilot.trigger('resource_center:jNfDAOvnub')
                              }
                            }}
                            className="error-link"
                            onClick={() => {
                              Userpilot.trigger('resource_center:jNfDAOvnub')
                            }}
                            rel="noopener noreferrer"
                          >
                            {translate('contact support')}
                          </span>
                        </>
                      ) : (
                        errorMessage
                      )}
                    </p>
                  </div>
                </div>
              )}
            </AlertPanel>
          )}
          <div className="flex-row justify-between button-container">
            <div
              className="font-linktext flex-row gap-2 align-center additional-info-link"
              role="button"
              onClick={onBack}
              tabIndex={-1}
              // For WAI-ARIA considerations
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onBack()
                }
              }}
            >
              <SvgChevronLeft />
              {goBackText ? goBackText : translate('Cancel')}
            </div>
            <Button
              className="confirm-button"
              data-testid="confirm-button"
              data-isloading={isSubmitting}
              loading={isSubmitting}
              disabled={isSubmitting || isSubmitDisabled() || !!errorMessage}
              onClick={handleSubmitData}
            >
              {translate('Confirm')}
            </Button>
          </div>
          {binData.issuerRequiresVerification && (
            <div className="flex-column gap-2">
              <p className="font-bold verification-required-header">
                {translate('Verification Required')}
              </p>
              <p className="verification-paragraph">
                {translate(
                  'After you confirm registration, a temporary charge containing a code will appear in your recent authorization activity with EX in the name or description.',
                )}
              </p>
              <ol className="verification-list">
                <li className="font-bold verification-list-item">
                  {translate(
                    'Find the code in your recent authorization activity.',
                  )}
                </li>
                <li className="font-bold verification-list-item">
                  {translate('Enter that 5-letter code into ${appName}.', {
                    appName,
                  })}
                </li>
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ReviewEnrollCard
